'use client'

import { useTranslations } from '@/lib/next-shim'
import { CheckIcon, TrashIcon } from '@heroicons/react/24/outline'
import type { SavedAddress } from '@/core/graphql/types'

export type { SavedAddress }

type SavedAddressListProps = {
  addresses: SavedAddress[]
  onSelect: (address: SavedAddress) => void
  onDelete?: (addressId: string) => void
}

export function SavedAddressList({ addresses, onSelect, onDelete }: SavedAddressListProps) {
  const t = useTranslations()

  if (!addresses.length) {
    return (
      <p className="text-sm text-gray-500 italic py-2">{t('address.noSavedAddresses')}</p>
    )
  }

  return (
    <ul className="divide-y divide-gray-200 rounded-md border border-gray-200 bg-white overflow-hidden">
      {addresses.map((address) => (
        <li key={address._id} className="flex items-center justify-between hover:bg-gray-50">
          <button
            type="button"
            className="flex-1 px-4 py-3 text-left"
            onClick={() => onSelect(address)}
          >
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-900">{address.label}</span>
              {address.isDefault && (
                <span className="inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">
                  <CheckIcon className="mr-0.5 h-3 w-3" />
                  {t('address.default')}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-0.5">
              {address.street}, {address.postalCode} {address.city}
            </p>
            {address.apartment && (
              <p className="text-xs text-gray-400 mt-0.5">
                {t('address.apartment')}: {address.apartment}
                {address.floor && ` · ${t('address.floor')}: ${address.floor}`}
              </p>
            )}
          </button>
          {onDelete && (
            <button
              type="button"
              className="p-2 mr-2 text-gray-400 hover:text-red-500"
              onClick={(e) => {
                e.stopPropagation()
                onDelete(address._id)
              }}
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          )}
        </li>
      ))}
    </ul>
  )
}
